export const addressFragment = `
	fragment AddressFragment on MailingAddress {
		id
		address1
		address2
		city
		company
		country
		countryCode: countryCodeV2
		countryCodeV2
		firstName
		lastName
		phone
		province
		provinceCode
		zip
	}
`;

const moneyFragment = `
	fragment MoneyFragment on MoneyV2 {
		amount
		currencyCode
	}
`;

const attributeFragment = `
	fragment AttributeFragment on Attribute {
		key
		value
	}
`;

/*
const appliedGiftCardFragment = `
	fragment AppliedGiftCardFragment on AppliedGiftCard {
		id
		lastCharacters
		amountUsed {
			...MoneyFragment
		}
		amountUsedV2: amountUsed {
			...MoneyFragment
		}
		balance {
			...MoneyFragment
		}
		balanceV2: balance {
			...MoneyFragment
		}
		presentmentAmountUsed {
			...MoneyFragment
		}
		presentmentAmountUsedV2: presentmentAmountUsed {
			...MoneyFragment
		}
	}
`;
*/

const discountApplicationFragment = `
	fragment DiscountApplicationFragment on DiscountApplication {
		allocationMethod
		targetSelection
		targetType
		value
	}
`;

const variantFragment = `
	fragment VariantFragment on ProductVariant {
		availableForSale
		compareAtPrice {
			...MoneyFragment
		}
		compareAtPriceV2: compareAtPrice {
			...MoneyFragment
		}
		id
		image {
			altText
			height
			width
			id
			url
			src: url
		}
		selectedOptions {
			name
			value
		}
		sku
		title
		price {
			...MoneyFragment
		}
		priceV2: price {
			...MoneyFragment
		}
		unitPrice {
			...MoneyFragment
		}
		unitPriceV2: unitPrice {
			...MoneyFragment
		}
	}
`;

const orderLineItemFragment = `
	fragment OrderLineItemFragment on OrderLineItem {
		quantity
		title
		currentQuantity
		customAttributes {
			...AttributeFragment
		}
		discountedTotalPrice {
			...MoneyFragment
		}
		discountedTotalPriceV2: discountedTotalPrice {
			...MoneyFragment
		}
		discountAllocations {
			allocatedAmount {
				...MoneyFragment
			}
			allocatedAmountV2: allocatedAmount {
				...MoneyFragment
			}
			discountApplication {
				...DiscountApplicationFragment
			}
		}
		originalTotalPrice {
			...MoneyFragment
		}
		originalTotalPriceV2: originalTotalPrice {
			...MoneyFragment
		}
		variant {
			...VariantFragment
			...on ProductVariant {
				product {
					handle
					id
					title
					tags
					priceRange {
						minVariantPrice {
							...MoneyFragment
						}
					}
					images(first: 250) {
						edges {
							node {
								altText
								originalSrc
							}
						}
					}
				}
			}
		}
	}
`;

const orderFragment = `
	fragment OrderFragment on Order {
		cancelReason
		canceledAt
		currencyCode
		customerLocale
		customerUrl
		edited
		email
		financialStatus
		fulfillmentStatus
		id
		name
		orderNumber
		phone
		processedAt
		discountApplications(first: 250) {
			edges {
				node {
					...DiscountApplicationFragment
				}
			}
		}
		lineItems(first: 250) {
			edges {
				node {
					...OrderLineItemFragment
				}
			}
		}
		shippingAddress {
			...AddressFragment
		}
		shippingDiscountAllocations {
			allocatedAmount {
				...MoneyFragment
			}
			discountApplication {
				...DiscountApplicationFragment
			}
		}
		subtotalPrice {
			...MoneyFragment
		}
		subtotalPriceV2: subtotalPrice {
			...MoneyFragment
		}
		successfulFulfillments(first: 250) {
			trackingCompany
			trackingInfo {
				number
				url
			}
		}
		totalPrice {
			...MoneyFragment
		}
		totalPriceV2: totalPrice {
			...MoneyFragment
		}
		totalRefunded {
			...MoneyFragment
		}
		totalRefundedV2: totalRefunded {
			...MoneyFragment
		}
		totalShippingPrice {
			...MoneyFragment
		}
		totalShippingPriceV2: totalShippingPrice {
			...MoneyFragment
		}
		currentTotalTax {
			...MoneyFragment
		}
		currentTotalTaxV2: currentTotalTax {
			...MoneyFragment
		}
		totalTax {
			...MoneyFragment
		}
		totalTaxV2: totalTax {
			...MoneyFragment
		}
	}
`;

export const customerOrdersFragment = `
	fragment CustomerOrdersFragment on Customer {
		orders(first: 250, reverse: true) {
			edges {
				node {
					...OrderFragment
				}
			}
		}
	}

	${orderFragment}
	${attributeFragment}
	${orderLineItemFragment}
	${variantFragment}
	${discountApplicationFragment}
	${moneyFragment}
`;

export const customerFragment = `
	fragment CustomerFragment on Customer {
		id
		firstName
		lastName   
		numberOfOrders  
		email
		phone
		# null unless the metafield definition has Storefront API access turned on
		paymentTerms: metafield(key: "payment_terms", namespace: "nordic") {
			value
		}
		defaultAddress {
			...AddressFragment
		}
		addresses(first: 250) {
			edges {
				node {
					...AddressFragment
				}
			}
		}
	}

	${addressFragment}
`;

const cartLineFragment = `
	fragment CartLineFragment on CartLine {
		id
		quantity
		attributes {
			...AttributeFragment
		}
		cost {
			amountPerQuantity {
				...MoneyFragment
			}
			compareAtAmountPerQuantity {
				...MoneyFragment
			}
			subtotalAmount {
				...MoneyFragment
			}
			totalAmount {
				...MoneyFragment
			}
			amountPerQuantityV2: amountPerQuantity {
				...MoneyFragment
			}
			compareAtAmountPerQuantityV2: compareAtAmountPerQuantity {
				...MoneyFragment
			}
			subtotalAmountV2: subtotalAmount {
				...MoneyFragment
			}
			totalAmountV2: totalAmount {
				...MoneyFragment
			}
		}
		customAttributes: attributes {
			...AttributeFragment
		}
		discountAllocations {
			discountedAmount {
				...MoneyFragment
			}
		}
		sellingPlanAllocation {
			checkoutChargeAmount {
				...MoneyFragment
			}
			priceAdjustments {
				compareAtPrice {
					...MoneyFragment
				}
				price {
					...MoneyFragment
				}
				unitPrice {
					...MoneyFragment
				}
				compareAtPriceV2: compareAtPrice {
					...MoneyFragment
				}
				priceV2: price {
					...MoneyFragment
				}
				unitPriceV2: unitPrice {
					...MoneyFragment
				}
			}
			sellingPlan {
				description
				id
				name
				recurringDeliveries
				checkoutCharge {
					type
					value
				}
				priceAdjustments {
					adjustmentValue
					orderCount
				}
			}
		}
		variant: merchandise {
			...VariantFragment
			...on ProductVariant {
				product {
					handle
					id
					title
				}
			}
		}
	}
`;

export const cartFragment = `
	fragment CartFragment on Cart {
		checkoutUrl
		createdAt
		id
		note
		totalQuantity
		updatedAt
		webUrl: checkoutUrl
		attributes {
			...AttributeFragment
		}
		cost {
			checkoutChargeAmount {
				...MoneyFragment
			}
			subtotalAmount {
				...MoneyFragment
			}
			totalAmount {
				...MoneyFragment
			}
			totalDutyAmount {
				...MoneyFragment
			}
			totalTaxAmount {
				...MoneyFragment
			}
			checkoutChargeAmountV2: checkoutChargeAmount {
				...MoneyFragment
			}
			subtotalAmountV2: subtotalAmount {
				...MoneyFragment
			}
			totalAmountV2: totalAmount {
				...MoneyFragment
			}
			totalDutyAmountV2: totalDutyAmount {
				...MoneyFragment
			}
			totalTaxAmountV2: totalTaxAmount {
				...MoneyFragment
			}
		}
		discountAllocations {
			discountedAmount {
				...MoneyFragment
			}
		}
		discountCodes {
			applicable
			code
		}
		lineItems: lines(first: 250) {
			edges {
				node {
					...CartLineFragment
				}
			}
		}
	}

	${attributeFragment}
	${moneyFragment}
	${variantFragment}
	${cartLineFragment}
`;

export const customerAccessTokenFragment = `
	fragment CustomerAccessTokenFragment on CustomerAccessToken {
		accessToken
		expiresAt
	}
`;

export const customerUserErrorFragment = `
	fragment CustomerUserErrorFragment on CustomerUserError {
		code
		field
		message
	}
`;

export const cartUserErrorFragment = `
	fragment CartUserErrorFragment on CartUserError {
		code
		field
		message
	}
`;

export const userErrorFragment = `
	fragment UserErrorFragment on UserError {
		field
		message
	}
`;

export const productAvailabilityFragment = `
	fragment ProductAvailabilityFragment on Product {
		id
		availableForSale
		variants(first: 250) {
			edges {
				node {
					id
					available: availableForSale
					availableForSale
					# quantityAvailable
				}
			}
		}
	}
`;

//  Usually used for Product Slider Components
export const productBasicFragment = `
	fragment ProductBasicFragment on Product {
		id
		title
		handle
		tags
		priceRange {
			minVariantPrice {
				amount
				currencyCode
			}
		}
		images(first: 1) {
			edges {
				node {
					altText
					originalSrc
				}
			}
		}
	}
`;
